import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { ChatMessage } from "@domestika-dev/prudencio/dist/atoms/ChatMessage";
import { ChatMessagesList } from "@domestika-dev/prudencio/dist/atoms/ChatMessagesList";
import { Box } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { config } from "../../../config";
import { LiveSessionContext } from "../../../context/LiveSessionContext";
import { sanitizeText } from "../../../../../../js/utils/functions";
import { getAssetPath } from "../../../utils/assetsUtils";

const getMessageType = ({ isOwnMessage, isTeacherMessage }) => {
  if (isOwnMessage) {
    return "secondary";
  }

  if (isTeacherMessage) {
    return "primary";
  }

  return "default";
};

/* eslint-disable-next-line react/prop-types */
const FakeChatMessage = ({ fakeChatMessageRef, ...props }) => (
  <Box
    ref={fakeChatMessageRef}
    width="100%"
    paddingX={4}
    style={{ position: "absolute", top: 0, visibility: "hidden" }}
  >
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <ChatMessage width="100%" {...props} avatar="" />
  </Box>
);

export const ChatMessages = ({ chatMessagesHeight }) => {
  const intl = useIntl();
  const [chatMessages, setChatMessages] = useState([]);
  const [renderFakeMessage, setRenderFakeMessage] = useState(false);
  const [fakeChatMessageProps, setFakeChatMessageProps] = useState(null);
  const { chatMessages$ } = useContext(LiveSessionContext);

  const addChatMessage = (message) => {
    if (message) {
      setChatMessages((oldMessages) => oldMessages.concat([message]));
      setRenderFakeMessage(false);
      setFakeChatMessageProps(null);
    }
  };

  const fakeChatMessageCalculationRef = (node) => {
    if (node !== null) {
      addChatMessage({
        ...fakeChatMessageProps,
        height: node.getBoundingClientRect().height,
      });
    }
  };

  const setupFakeMessage = (message) => {
    setFakeChatMessageProps(message);
    setRenderFakeMessage(true);
  };

  const observeNewChatMessages = useCallback(({ message, isOwnMessage, isTeacherMessage }) => {
    setupFakeMessage({
      avatar: getAssetPath(message.avatar),
      author: message.author,
      message: sanitizeText(message.body),
      type: getMessageType({ isOwnMessage, isTeacherMessage }),
    });
  }, []);

  const sendWelcomeMessage = () => {
    setupFakeMessage({
      avatar: getAssetPath(config.DEFAULT_DOMESTIKA_STREAMER_AVATAR),
      author: config.DEFAULT_DOMESTIKA_STREAMER_NAME,
      message: intl.formatMessage({
        id: "live_sessions.chat.messages.welcome_message",
      }),
      type: "default",
    });
  };

  useEffect(() => {
    sendWelcomeMessage();
    chatMessages$.subscribe(observeNewChatMessages);
    return () => {
      chatMessages$.unsubscribe(observeNewChatMessages);
    };
  }, []);

  return (
    <div style={{ position: "relative" }}>
      {renderFakeMessage && (
        <FakeChatMessage
          fakeChatMessageRef={fakeChatMessageCalculationRef}
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...fakeChatMessageProps}
        />
      )}
      <ChatMessagesList
        height={chatMessagesHeight}
        width="100%"
        goToLastButtonMessage={intl.formatMessage({
          id: "live_sessions.chat.messages.see_last_message_button",
        })}
        chatMessages={chatMessages}
      />
    </div>
  );
};

ChatMessages.propTypes = {
  chatMessagesHeight: PropTypes.string,
};

ChatMessages.defaultProps = {
  chatMessagesHeight: "",
};

export default ChatMessages;
